import { Transition } from '@headlessui/react';
import React, { Dispatch, FC, useContext } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  CheckCircleIcon,
  ChevronDownIcon,
  ExcerciseIcon,
  ExportIcon,
  PlusIcon,
} from '../../assets/icons/index';
import { AlertDialogWrapper } from '../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button, { ButtonDisableStyle } from '../../components/button/index';
import { Radio, RadioGroup } from '../../components/radio-group/index';
import { Select, SelectButton, SelectOption, SelectOptions } from '../../components/select/index';
import { Tooltip } from '../../components/tooltip/index';
import { sideBarContext } from '../../context/side-bar-context';
import { SharePlan, useLocale, useModalState, useScreenSize } from '../../hooks/index';
import { TargetBasedVestingHookMode } from '../../hooks/share-plan/index';
import {
  Pool,
  PoolPlansTab,
  PoolsPlansFilter,
  PoolsPlansSelect,
  SelectVariant,
} from '../../types/pool-plans.types';
import { ApproveTargetModal } from './modals/approve-target/index';
import { ExerciseModal } from './modals/exercise/index';

export type PoolPlansHeaderProps = {
  date: Date;
  onAddPlanClick?: () => void;
  onAddPoolClick?: () => void;
  onExportClick?: () => void;
  onOpenExerciseStockModal: () => void;
  pools: Pool[];
  selected: SelectVariant | undefined;
  selectedFilter: PoolsPlansFilter;
  selectedTab: PoolPlansTab;
  setDate: (date: Date) => void;
  setSelected: Dispatch<React.SetStateAction<SelectVariant | undefined>>;
  setSelectedFilter: (filter: PoolsPlansFilter) => void;
};

const selectVariant = Object.values(PoolsPlansSelect);

const PoolPlansHeader: FC<PoolPlansHeaderProps> = ({
  setSelectedFilter,
  setSelected,
  selectedFilter,
  selected,
  selectedTab,
  onOpenExerciseStockModal,
  onAddPoolClick,
  onAddPlanClick,
  onExportClick,
  pools: _pools,
  date: _date,
  setDate: _setDate,
}) => {
  const { messagesLocale } = useLocale();
  const { isOpen: isSideBarExpanded } = useContext(sideBarContext);
  const { width } = useScreenSize();

  const handleSelect = (select: SelectVariant) => {
    if (select === PoolsPlansSelect.NEW_POOL) onAddPoolClick?.();
    if (select === PoolsPlansSelect.NEW_PLAN) onAddPlanClick?.();
    if (select === 'export') onExportClick?.();

    setSelected(select);
  };

  const { targetBasedVesting } =
    SharePlan.useTargetBasedVesting<TargetBasedVestingHookMode.CHECK_EXIST>({
      mode: TargetBasedVestingHookMode.CHECK_EXIST,
    });

  const {
    handleCloseModal: handleCloseExerciseModal,
    handleOpenModal: handleOpenExerciseModal,
    isOpen: isOpenExerciseModal,
    toggler: exerciseModalToggler,
  } = useModalState({});

  const {
    handleCloseModal: handleCloseApproveTargetModal,
    handleOpenModal: handleOpenApproveTargetModal,
    isOpen: isOpenApproveTargetModal,
    toggler: approveTargetModalToggler,
  } = useModalState({});

  const isTablet = width <= 1233;

  return (
    <>
      <AlertDialogWrapper
        control={{ onOpenChange: exerciseModalToggler, open: isOpenExerciseModal }}
      >
        <ExerciseModal
          onClose={handleCloseExerciseModal}
          onOpenExerciseStockModal={onOpenExerciseStockModal}
        />
      </AlertDialogWrapper>

      <AlertDialogWrapper
        control={{ onOpenChange: approveTargetModalToggler, open: isOpenApproveTargetModal }}
      >
        <ApproveTargetModal onClose={handleCloseApproveTargetModal} />
      </AlertDialogWrapper>

      <div className="flex w-full items-center justify-between px-3">
        <RadioGroup
          className="h-fit w-full divide-x divide-gray-200 overflow-hidden rounded border border-gray-200"
          onChange={setSelectedFilter}
          value={selectedFilter}
        >
          <Radio
            className="flex h-9 w-full items-center justify-center px-4 text-sm font-[450] text-[#858593] data-[checked]:bg-brand-25 data-[checked]:font-[550] data-[checked]:text-[#172335]"
            onCheckedDisableClassName="data-[checked]:bg-brand-25 data-[checked]:text-gray-300 cursor-default"
            styleType="NONE"
            value={PoolsPlansFilter.BY_POOLS}
          >
            <span
              className={twMerge('overflow-hidden text-ellipsis whitespace-nowrap text-gray-700')}
            >
              <AppFormattedMessage id="BY_POOLS" />
            </span>
          </Radio>
          {selectedTab === PoolPlansTab.ACTIVE && (
            <Radio
              className="flex h-9 w-full items-center justify-center px-4 text-sm font-[450] text-[#858593] data-[checked]:bg-brand-25 data-[checked]:font-[550] data-[checked]:text-[#172335]"
              onCheckedDisableClassName="data-[checked]:bg-brand-25 data-[checked]:text-gray-300 cursor-default"
              styleType="NONE"
              value={PoolsPlansFilter.BY_STAKEHOLDER}
            >
              <span
                className={twMerge('overflow-hidden text-ellipsis whitespace-nowrap text-gray-700')}
              >
                <AppFormattedMessage id="BY_STAKEHOLDER" />
              </span>
            </Radio>
          )}
        </RadioGroup>
        <div className="flex max-h-[52px] items-center gap-2 py-2">
          {/* <span className="w-fit whitespace-nowrap text-sm font-[450] text-gray-700">
            {isSideBarExpanded && isTablet ? '' : <AppFormattedMessage id="VEST_BY" />}
          </span> */}
          {/* <FormDatePicker
            calendar={{ disabled: { before: startOfDay(new Date()) } }}
            inputClassName="h-[34px]"
            inputValue={date ? format(date, 'dd/MM/yyyy') : ''}
            inputWrapperClassName="w-full h-[34px]"
            onSelect={(date) => {
              setDate(date);
            }}
            value={date}
            wrapperClassName="w-[158px] h-[34px]"
          /> */}
          {!isTablet && (
            <Tooltip
              content={messagesLocale['NO_PENDING_TARGET_BASED_VESTING']}
              disabled={isOpenApproveTargetModal || targetBasedVesting?.isValid}
            >
              <Button
                className={twMerge(
                  'flex size-9 h-[36px] gap-1 bg-gray-25 px-3 py-[6px] ring-1 ring-gray-100 lg:w-fit',
                )}
                data-permission-required
                disabled={!targetBasedVesting?.isValid}
                disabledStyle={ButtonDisableStyle.INACTIVE}
                onClick={handleOpenApproveTargetModal}
              >
                <CheckCircleIcon iconColor={!targetBasedVesting?.isValid ? '#D0D5DD' : undefined} />
                <span
                  className={twMerge(
                    'text-sm font-[450] text-gray-700 max-lg:hidden',
                    !targetBasedVesting?.isValid && 'text-gray-300',
                  )}
                >
                  <AppFormattedMessage id="APPROVE_VESTING" />
                </span>
              </Button>
            </Tooltip>
          )}

          <Button
            className="flex size-9 gap-1 border-[1px] border-gray-100 bg-gray-25 px-3 py-[6px] lg:w-fit"
            data-permission-required
            onClick={handleOpenExerciseModal}
          >
            <ExcerciseIcon />
            <span className="text-sm font-[450] text-gray-700 max-lg:hidden">
              {isSideBarExpanded && isTablet ? '' : <AppFormattedMessage id="EXERCISE" />}
            </span>
          </Button>
          <Button
            className="flex size-9 gap-1 border-[1px] border-gray-100 bg-gray-25 px-3 py-[6px] lg:w-fit"
            onClick={() => handleSelect('export')}
          >
            <ExportIcon />
            <span className="text-sm font-[450] text-gray-700 max-lg:hidden">
              <AppFormattedMessage id="EXPORT" />
            </span>
          </Button>
          <Select onChange={handleSelect} value={selected}>
            {({ open }) => (
              <>
                <div className="relative rounded border-gray-200 px-[6px]" data-permission-required>
                  <SelectButton className="bg-brand-700">
                    <PlusIcon />
                    <span className="text-sm font-[450] text-white">
                      <AppFormattedMessage id="ADD" />
                    </span>
                    <ChevronDownIcon className="ml-2 h-4 w-4" iconColor="#ffffff" />
                  </SelectButton>

                  <Transition
                    as="div"
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    show={open}
                  >
                    <SelectOptions className="w-fit">
                      {selectVariant.map((select) => (
                        <SelectOption key={select} selectedClassName="bg-white" value={select}>
                          <span className="block text-sm font-normal text-gray-700">
                            <AppFormattedMessage
                              id={select === 'new plan' ? 'NEW_PLAN' : 'NEW_POOL'}
                            />
                          </span>
                        </SelectOption>
                      ))}
                    </SelectOptions>
                  </Transition>
                </div>
              </>
            )}
          </Select>
        </div>
      </div>
    </>
  );
};

export default PoolPlansHeader;
